import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IEscalationSupportingDocument } from "../../../../setup/api/services/contract/models";
import styles from "./SupportingDocumentsModal.module.scss";

interface Props {
  open: boolean;
  onClose: () => void;
  documents: IEscalationSupportingDocument[];
}

const IMAGE_EXT = /\.(png|jpg|jpeg|gif|webp|svg)(\?.*)?$/i;

const getFilename = (doc: IEscalationSupportingDocument): string => {
  if (doc.title) return doc.title;
  if (doc.objectName) return doc.objectName.split("/").pop() || doc.objectName;
  if (doc.url) return doc.url.split("/").pop()?.split("?")[0] || "مرفق";
  return "مرفق";
};

const SupportingDocumentsModal = ({ open, onClose, documents }: Props) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h4 className={styles.title}>{t("lbl_attachments")}</h4>
            <span className={styles.countBadge}>{documents.length}</span>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.divider} />

        <div className={styles.docList}>
          {documents.map((doc, i) => {
            const filename = getFilename(doc);
            const isImage = IMAGE_EXT.test(filename);
            return (
              <a
                key={i}
                href={doc.url || undefined}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.docItem}>
                {isImage ? (
                  <div className={styles.thumbWrapper}>
                    <img
                      src={doc.url || ""}
                      alt={filename}
                      className={styles.thumb}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.fileIconWrapper}>📄</div>
                )}
                <div className={styles.docMeta}>
                  <span className={styles.docName}>{filename}</span>
                  <span className={styles.openLink}>{t("lbl_open_attachment")}</span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SupportingDocumentsModal;
